import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Upload, Link as LinkIcon, Image, Film, Sparkles, Eye, EyeOff } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDIA_TYPES = [
  { value: 'photo', label: 'Photo', icon: Image },
  { value: 'video', label: 'Video', icon: Film },
  { value: 'marquee', label: 'Marquee Photo', icon: Sparkles },
];
const empty = { title: '', description: '', media_url: '', media_type: 'photo', is_active: true };

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [imageMode, setImageMode] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fetchItems = () => {
    setLoading(true);
    axios.get(`${API}/admin/gallery`, { withCredentials: true })
      .then(r => setItems(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew = (type = 'photo') => {
    setEditItem(null);
    setForm({ ...empty, media_type: type });
    setImageMode('upload');
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...item });
    setImageMode(item.media_url ? 'url' : 'upload');
    setDialogOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, GIF, MP4, WebM allowed');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large (max 50MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, media_url: data.image_url }));
      toast.success('Uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);

  const handleSave = async () => {
    if (!form.media_url) { toast.error('Please provide a media URL or upload a file'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/gallery/${editItem.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/gallery`, form, { withCredentials: true });
      }
      toast.success(editItem ? 'Updated' : 'Created');
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const filtered = filter === 'all' ? items : items.filter(i => i.media_type === filter);
  const counts = { all: items.length, photo: items.filter(i => i.media_type === 'photo').length, video: items.filter(i => i.media_type === 'video').length, marquee: items.filter(i => i.media_type === 'marquee').length };

  return (
    <AdminLayout active="/admin/gallery">
      <div data-testid="admin-gallery">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-heading text-3xl font-bold text-dark-brown">Our Work</h1>
          <div className="flex gap-2">
            {MEDIA_TYPES.map(t => (
              <Button key={t.value} onClick={() => openNew(t.value)} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full text-xs" data-testid={`add-${t.value}-btn`}>
                <Plus className="w-3.5 h-3.5 mr-1" /> {t.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {[{ value: 'all', label: 'All' }, ...MEDIA_TYPES.map(t => ({ value: t.value, label: t.label }))].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`px-4 py-2 rounded-full font-body text-sm font-medium transition-all ${
                filter === tab.value
                  ? 'bg-rust text-cream shadow-md'
                  : 'bg-white text-mid-brown border border-border-custom hover:border-rust/30 hover:text-rust'
              }`}
            >
              {tab.label} ({counts[tab.value] || 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🖼️</div>
            <p className="font-body text-light-brown text-lg mb-2">No items yet</p>
            <p className="font-body text-sm text-light-brown/70 mb-6">Upload photos, videos, or marquee images to showcase your work</p>
            <Button onClick={() => openNew()} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add First Item
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map(item => (
              <div key={item.id} className="group relative bg-white rounded-xl border border-border-custom overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid={`gallery-item-${item.id}`}>
                <div className="relative aspect-square overflow-hidden bg-pale/30">
                  {item.media_type === 'video' ? (
                    <video src={item.media_url} className="w-full h-full object-cover" muted preload="metadata" />
                  ) : (
                    <img src={item.media_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=300&h=300&fit=crop'; }} />
                  )}
                  {/* Type badge */}
                  <span className={`absolute top-3 left-3 text-xs font-body font-medium px-2.5 py-1 rounded-full ${
                    item.media_type === 'video' ? 'bg-purple-600 text-white' :
                    item.media_type === 'marquee' ? 'bg-amber text-white' :
                    'bg-rust text-cream'
                  }`}>
                    {item.media_type === 'marquee' ? 'Marquee' : item.media_type === 'video' ? 'Video' : 'Photo'}
                  </span>
                  {!item.is_active && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-body font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                      <EyeOff className="w-3 h-3" /> Hidden
                    </span>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-dark-brown/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                    <button onClick={() => openEdit(item)} className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-rust hover:bg-white transition-colors" data-testid={`edit-gallery-${item.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-red-500 hover:bg-white transition-colors" data-testid={`delete-gallery-${item.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {item.title && (
                  <div className="p-3">
                    <h3 className="font-body text-sm font-medium text-dark-brown line-clamp-1">{item.title}</h3>
                    {item.description && <p className="font-body text-xs text-light-brown line-clamp-2 mt-0.5">{item.description}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-dark-brown">
                {editItem ? 'Edit' : 'Add'} {form.media_type === 'marquee' ? 'Marquee Photo' : form.media_type === 'video' ? 'Video' : 'Photo'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="font-body text-sm">Title (optional)</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 bg-cream/50" placeholder="e.g. Persian grooming session" />
              </div>
              <div>
                <Label className="font-body text-sm">Description (optional)</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 bg-cream/50" rows={2} placeholder="Brief description..." />
              </div>
              <div>
                <Label className="font-body text-sm">Type</Label>
                <Select value={form.media_type} onValueChange={v => setForm({...form, media_type: v})}>
                  <SelectTrigger className="mt-1 bg-cream/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MEDIA_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Media upload */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-body text-sm">Media</Label>
                  <div className="flex bg-cream/80 rounded-full p-0.5">
                    <button type="button" onClick={() => setImageMode('upload')}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body transition-all ${imageMode === 'upload' ? 'bg-rust text-cream shadow-sm' : 'text-mid-brown hover:text-dark-brown'}`}>
                      <Upload className="w-3 h-3" /> Upload
                    </button>
                    <button type="button" onClick={() => setImageMode('url')}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body transition-all ${imageMode === 'url' ? 'bg-rust text-cream shadow-sm' : 'text-mid-brown hover:text-dark-brown'}`}>
                      <LinkIcon className="w-3 h-3" /> URL
                    </button>
                  </div>
                </div>

                {imageMode === 'upload' ? (
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
                      ${dragActive ? 'border-rust bg-rust/5 scale-[1.01]' : 'border-border-custom hover:border-rust/50 hover:bg-cream/30'}
                      ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                    onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])} />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="spinner"></div>
                        <p className="font-body text-sm text-mid-brown">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-rust/10 flex items-center justify-center">
                          {form.media_type === 'video' ? <Film className="w-6 h-6 text-rust" /> : <Image className="w-6 h-6 text-rust" />}
                        </div>
                        <p className="font-body text-sm text-dark-brown font-medium">
                          {dragActive ? 'Drop file here!' : 'Click or drag file here'}
                        </p>
                        <p className="font-body text-xs text-light-brown">
                          {form.media_type === 'video' ? 'MP4, WebM · Max 50MB' : 'JPG, PNG, WebP, GIF · Max 50MB'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <Input value={form.media_url} onChange={e => setForm({...form, media_url: e.target.value})} className="bg-cream/50" placeholder="https://example.com/media.jpg" />
                )}

                {/* Preview */}
                {form.media_url && (
                  <div className="mt-3 relative group">
                    {form.media_type === 'video' ? (
                      <video src={form.media_url} className="w-full h-40 object-cover rounded-xl border border-border-custom" controls />
                    ) : (
                      <img src={form.media_url} alt="Preview" className="w-full h-40 object-cover rounded-xl border border-border-custom"
                        onError={e => { e.target.style.display = 'none'; }} />
                    )}
                    <button type="button" onClick={() => setForm({...form, media_url: ''})}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-50">
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
                <Label className="font-body text-sm">Visible on website</Label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-rust hover:bg-rust/90 text-cream font-body rounded-full">
                  {saving ? 'Saving...' : editItem ? 'Update' : 'Add'}
                </Button>
                <Button onClick={() => setDialogOpen(false)} variant="outline" className="font-body rounded-full">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
